// Asset ID page: the rules behind the form. Pure: answers in, Asset ID and record out.
// Words: ASSET_ID_PAGE_WORDS.md (approved by Robert 2026-10-21). Mockup: MOCKUP_ASSET_ID_PAGE.html.
// The workbook is the standard: Field Asset ID Creator.xlsx, tab Field Risk Assessment Form,
// rows 6 to 8 and its lookup tables. Every answer here is figured the way the workbook figures it,
// and the record uses the field names the asset push writes (safetag_assets.py v6.1 FIELDS).
// Unit tested against every Baconton row: tests/unit/asset-id.test.ts.

// ---- The workbook's lists ----

/** Equipment Location (AK:AL). Extended rows use the same codes as their room or area. */
export const LOCATIONS: readonly { name: string; code: string }[] = [
  { name: "Electrical Room", code: "ER" },
  { name: "Electrical Area", code: "EA" },
  { name: "Electrical Room-Extended", code: "ER" },
  { name: "Electrical Area-Extended", code: "EA" },
  { name: "Outside", code: "" },
];

/** Nominal System Voltage (BF8:BF13). */
export const VOLTAGES = ["Voltage <50", "120V (1-P)", "208V (P-P)", "240V (P-P)", "480V (P-P)", "600V (P-P)"] as const;
export const CONDUCTORS = ["Fixed Conductor", "Moveable Conductor"] as const;
export const YES_NO = ["YES", "NO"] as const;

export interface ClassOption {
  name: string;
  code: string;
}

/** The workbook's EET list (AN:AO), for a device whose site pack has no Equipment Classes copy yet. */
export const WORKBOOK_CLASSES: readonly ClassOption[] = [
  ["Switchboard", "SWBD"], ["Switchgear", "SWGR"], ["Motor Control Center", "MCC"], ["Panelboard", "PNL"],
  ["Transformer", "XFMR"], ["Variable Frequency Drive", "VFD"], ["Industrial Control Panel", "ICP"],
  ["Disconnect Switch", "DS"], ["Fused Disconnect Switch", "FDS"], ["Non-Fused Disconnect Switch", "NFDS"],
  ["Automatic Transfer Switch", "ATS"], ["Electrical Enclosure", "ENCL"], ["Generator", "GEN"], ["UPS System", "UPS"],
  ["Power Factor Capacitor", "PFC"], ["Line Reactor", "LR"], ["Soft Starter", "SS"], ["Main Disconnect", "MD"],
  ["Main Service Panel", "MSP"], ["Main Service Disconnect", "MSD"], ["Main Distribution Panel", "MDP"],
  ["Capacitor Bank", "CAP"], ["Control Panel", "CP"], ["Junction Box", "JBOX"], ["Manual Motor Starter", "MS"], // MS, as the Lists page and site 04 have it (Robert, 2026-10-29 9:40 AM)
  ["Utility Service", "UTL"], ["Auxiliary Gutter", "GUT"],
].map(([name, code]) => ({ name: name!, code: code! }));

/** Limited and Restricted Approach (AV:AY), by voltage and conductor. */
const BOUNDARIES: Record<string, Record<string, [string, string]>> = {
  "Voltage <50": { "Fixed Conductor": ["Not specified", "Not specified"], "Moveable Conductor": ["Not specified", "Not specified"] },
  "120V (1-P)": { "Fixed Conductor": ["3 ft 6 in.", "Avoid Contact"], "Moveable Conductor": ["10 ft 0 in.", "Avoid Contact"] },
};
for (const v of ["208V (P-P)", "240V (P-P)", "480V (P-P)", "600V (P-P)"]) {
  BOUNDARIES[v] = { "Fixed Conductor": ["3 ft 6 in.", "1 ft 0 in."], "Moveable Conductor": ["10 ft 0 in.", "1 ft 0 in."] };
}

const SEVERITY: Record<string, number> = { "Voltage <50": 1, "120V (1-P)": 2, "208V (P-P)": 3, "240V (P-P)": 3, "480V (P-P)": 4, "600V (P-P)": 4 };

// ---- The form ----

/** One row of the Field Risk Assessment Form, as typed and picked. Blank is "". */
export interface IdForm {
  /** Sites Equipment Tag (A): the plant's own name for it, as the door or the study reads it. Not part of the ID. */
  tag: string;
  /** Equipment Location (D). */
  loc: string;
  /** Location Number (E). */
  locNum: string;
  /** Equipment Type (G): the class name. */
  type: string;
  /** Equipment Number (H). */
  num: string;
  /** Equipment "END" Location (J). Blank is the housing itself. */
  pos: string;
  volt: string;
  cond: string;
  passive: string;
  active: string;
  installed: string;
  usedPer: string;
  /** Out of Service (Z). */
  oos: string;
}

export const BLANK_FORM: IdForm = { tag: "", loc: "", locNum: "", type: "", num: "", pos: "", volt: "", cond: "", passive: "", active: "", installed: "", usedPer: "", oos: "NO" };

/** Letters and numbers only. Anything else typed is dropped, so a typo cannot make a malformed ID. */
export function cleanPart(v: string): string {
  return String(v ?? "").replace(/[^A-Za-z0-9]/g, "");
}

/**
 * The workbook's TEXT(x,"00"): a number is two digits at least (1 → 01, 12 → 12, 001 → 01),
 * text is kept as typed (1A → 1A, B4 → B4).
 */
export function padPart(v: string): string {
  const t = cleanPart(v);
  if (!t) return "";
  return /^\d+$/.test(t) ? String(Number(t)).padStart(2, "0") : t;
}

/** Location Number as the workbook joins it (E as typed; a number loses leading zeros). */
function locNumber(v: string): string {
  const t = cleanPart(v);
  return /^\d+$/.test(t) ? String(Number(t)) : t;
}

export function locationCode(loc: string): string {
  return LOCATIONS.find((l) => l.name === loc)?.code ?? "";
}

export function isOutside(f: Pick<IdForm, "loc">): boolean {
  return f.loc === "Outside";
}

export function classCode(type: string, classes: readonly ClassOption[]): string {
  return classes.find((c) => c.name === type)?.code ?? "";
}

/** The room the asset sits in: ER1, EA2, or Outside. Blank until the location is filled in. */
export function roomOf(f: Pick<IdForm, "loc" | "locNum">): string {
  if (isOutside(f)) return "Outside";
  const code = locationCode(f.loc);
  const n = locNumber(f.locNum);
  return code && n ? `${code}${n}` : "";
}

/**
 * The Asset ID, column K. Outside: SS-EET. Otherwise SS-EEL-EET, plus -POS when there is a position.
 * EEL is the location code and number (ER1). EET is the class code and the padded number (MCC-01).
 * Blank until every part it needs is filled in.
 */
/** The Equipment Type dropdown: Equipment Classes from the Lists page, else the workbook's list. */
export function classesOf(pack: { classes?: { code: string; name: string; order: number | null; isActive: boolean }[] }): ClassOption[] {
  const list = (pack.classes ?? []).filter((c) => c.isActive && c.name && c.code);
  const src = list.length ? [...list].sort((a, b) => (a.order ?? 999) - (b.order ?? 999) || a.name.localeCompare(b.name)) : WORKBOOK_CLASSES;
  const seen = new Set<string>();
  return src.filter((c) => (seen.has(c.name) ? false : (seen.add(c.name), true))).map((c) => ({ name: c.name, code: c.code }));
}

export function assetIdOf(site: string, f: IdForm, classes: readonly ClassOption[]): string {
  const code = classCode(f.type, classes);
  const num = padPart(f.num);
  if (!site || !code || !num) return "";
  const eet = `${code}-${num}`;
  if (isOutside(f)) return `${site}-${eet}`;
  const room = roomOf(f);
  if (!room) return "";
  const pos = f.pos.trim() === "@" ? "" : padPart(f.pos);
  return pos ? `${site}-${room}-${eet}-${pos}` : `${site}-${room}-${eet}`;
}

/** The location text saved on the record: "Electrical Room: ER1", "Outside". */
export function locationText(f: Pick<IdForm, "loc" | "locNum">): string {
  if (isOutside(f)) return "Outside";
  const room = roomOf(f);
  return room ? `${f.loc}: ${room}` : "";
}

// ---- Shock (columns N to Y) ----

export interface Shock {
  hazard: "YES" | "NO" | "";
  likelihood: number | null;
  severity: number | null;
  rpn: number | null;
  lab: string;
  rab: string;
  protection: "YES" | "NO" | "";
  residual: number | null;
}

export function shockOf(f: Pick<IdForm, "volt" | "cond" | "passive" | "active">): Shock {
  const hazard: Shock["hazard"] = f.volt in SEVERITY ? (f.volt === "Voltage <50" ? "NO" : "YES") : "";
  const likelihood = f.passive && f.active ? (f.active === "YES" ? 3 : f.passive === "YES" ? 2 : 1) : null;
  const severity = SEVERITY[f.volt] ?? null;
  const rpn = likelihood !== null && severity !== null ? Math.max(likelihood * severity, 1) : null;
  const b = BOUNDARIES[f.volt]?.[f.cond] ?? ["", ""];
  const protection = hazard;
  const residual = rpn === null ? null : protection === "YES" ? Math.max(rpn - 2, 1) : rpn;
  return { hazard, likelihood, severity, rpn, lab: b[0], rab: b[1], protection, residual };
}

/** The fields Save needs that are still blank, by the form's labels. The push refuses a blank answer too. */
export function missingFields(f: IdForm): string[] {
  const need: [keyof IdForm, string][] = [
    ["loc", "Equipment Location"],
    ...(isOutside(f) || !f.loc ? [] : ([["locNum", "Location Number"]] as [keyof IdForm, string][])),
    ["type", "Equipment Type"],
    ["num", "Equipment Number"],
    ["volt", "Nominal System Voltage"],
    ["cond", "Fixed or Moveable Conductor"],
    ["passive", "Passive Exposure"],
    ["active", "Active Exposure"],
    ["installed", "Properly Installed"],
    ["usedPer", "Used Per Manufacturer Instructions"],
    ["oos", "Out of Service"],
  ];
  return need.filter(([k]) => !cleanPart(String(f[k] ?? "")) && !String(f[k] ?? "").trim()).map(([, label]) => label);
}

// ---- The record (safetag_assets.py FIELDS) ----

const yes = (v: string): boolean | undefined => (v === "YES" ? true : v === "NO" ? false : undefined);

/** "480V (P-P)" → 480. "Voltage <50" → null. The push's volts(). */
export function voltsOf(nominalVoltage: string): number | null {
  const m = /^\s*(\d+)/.exec(nominalVoltage);
  return m ? Number(m[1]) : null;
}

/**
 * The asset push's 19 fields plus equipmentType, equipmentCode and nominalVolts, as the push writes
 * them. A blank answer is left out, as the push leaves it out of a new asset.
 */
export function assetFieldsOf(f: IdForm, classes: readonly ClassOption[]): Record<string, unknown> {
  const s = shockOf(f);
  const out: Record<string, unknown> = {
    siteEquipmentTag: tagText(f.tag),
    location: locationText(f),
    equipmentType: f.type,
    equipmentCode: classCode(f.type, classes),
    isOutOfService: yes(f.oos),
    nominalVoltage: f.volt,
    nominalVolts: voltsOf(f.volt),
    conductorType: f.cond,
    hasShockHazard: yes(s.hazard),
    hasPassiveExposure: yes(f.passive),
    hasActiveExposure: yes(f.active),
    isProperlyInstalled: yes(f.installed),
    isUsedPerInstructions: yes(f.usedPer),
    shockLikelihood: s.likelihood,
    shockSeverity: s.severity,
    shockRpn: s.rpn,
    limitedApproach: s.lab,
    restrictedApproach: s.rab,
    isShockProtectionRequired: yes(s.protection),
    shockResidualRpn: s.residual,
  };
  for (const k of Object.keys(out)) if (out[k] === undefined || out[k] === null || out[k] === "") delete out[k];
  return out;
}

/** Sites Equipment Tag as saved: kept as typed, trimmed, at most TAG_MAX characters. */
export const TAG_MAX = 40;
export function tagText(t: string): string {
  return String(t ?? "").replace(/\s+/g, " ").trim().slice(0, TAG_MAX);
}

/** The fields an edit may change (rule 31): the shock answers, Out of Service and the Sites Equipment Tag. */
export const EDIT_FIELDS = [
  "siteEquipmentTag",
  "isOutOfService", "nominalVoltage", "nominalVolts", "conductorType", "hasShockHazard", "hasPassiveExposure",
  "hasActiveExposure", "isProperlyInstalled", "isUsedPerInstructions", "shockLikelihood", "shockSeverity",
  "shockRpn", "limitedApproach", "restrictedApproach", "isShockProtectionRequired", "shockResidualRpn",
] as const;

export interface Stamp {
  siteNumber: string;
  badgeId: string;
  holderName: string;
  at: string;
}

/**
 * A new asset, with the same starting values the push gives a new one. fromWalk: the walk on this iPad makes this
 * Asset ID, so the row says it came from Field Labels (ASSESSMENT_FLOW_B7_PART2_WORDS.md, option A, 2026-10-29).
 */
export function newAssetRecord(assetId: string, f: IdForm, classes: readonly ClassOption[], s: Stamp, fromWalk = false): Record<string, unknown> {
  return {
    ...(fromWalk ? { addedFrom: "Field Labels" } : {}),
    assetId,
    siteNumber: s.siteNumber,
    ...assetFieldsOf(f, classes),
    ieStatus: "IE pending study",
    isRemoved: false,
    createdAt: s.at,
    createdBy: s.badgeId,
    createdByName: s.holderName,
    source: "Asset ID page",
  };
}

/** An edit: only the fields that changed, and who changed them. Null when nothing changed. */
export function editRecord(before: Record<string, unknown>, f: IdForm, classes: readonly ClassOption[], s: Stamp): Record<string, unknown> | null {
  const now = assetFieldsOf(f, classes);
  const out: Record<string, unknown> = {};
  for (const k of EDIT_FIELDS) {
    if (k in now && now[k] !== before[k]) out[k] = now[k];
  }
  // A tag cleared to blank is written empty, so it really comes off (ASSET_ID_TAG_COLUMN_WORDS.md). Other blank answers stay as they were.
  if (!("siteEquipmentTag" in now) && typeof before.siteEquipmentTag === "string" && before.siteEquipmentTag !== "") out.siteEquipmentTag = "";
  if (!Object.keys(out).length) return null;
  return { ...out, updatedAt: s.at, updatedBy: s.badgeId, updatedByName: s.holderName };
}

/** The form for an asset already made: its parts from the Asset ID and location, its answers from the record. */
export function formOfRecord(assetId: string, d: Record<string, unknown>, classes: readonly ClassOption[]): IdForm {
  const p = assetId.split("-");
  const loc = String(d.location ?? "");
  const outside = !/^Electrical/i.test(loc) && !/^E[RA]\d/i.test(p[1] ?? "");
  const code = String(d.equipmentCode ?? "") || (outside ? p[1] : p[2]) || "";
  const type = classes.find((c) => c.code === code)?.name ?? String(d.equipmentType ?? "");
  const locName = outside ? "Outside" : (LOCATIONS.find((l) => loc.startsWith(`${l.name}:`))?.name ?? (p[1]?.startsWith("EA") ? "Electrical Area" : "Electrical Room"));
  const yn = (v: unknown) => (v === true ? "YES" : v === false ? "NO" : "");
  return {
    tag: String(d.siteEquipmentTag ?? ""),
    loc: locName,
    locNum: outside ? "" : (p[1] ?? "").replace(/^E[RA]/i, ""),
    type,
    num: (outside ? p[2] : p[3]) ?? "",
    pos: outside ? "" : p.slice(4).join("-"),
    volt: String(d.nominalVoltage ?? ""),
    cond: String(d.conductorType ?? ""),
    passive: yn(d.hasPassiveExposure),
    active: yn(d.hasActiveExposure),
    installed: yn(d.isProperlyInstalled),
    usedPer: yn(d.isUsedPerInstructions),
    oos: yn(d.isOutOfService) || "NO",
  };
}

// ---- Repeat and index up (section 6) ----

/** A1 → A2, C9 → C10, 05 → 06, split bucket A2a → A2b. Section: A3 → B1. Null when there is no next one. */
export function nextPosition(pos: string, section: boolean): string | null {
  const t = cleanPart(pos);
  // A split bucket (Springfield's A2a, A2b) steps its last letter, not its number.
  const split = /^([A-Za-z])(\d+)([A-Ya-y])$/.exec(t);
  if (split && !section) return `${split[1]!.toUpperCase()}${split[2]}${String.fromCharCode(split[3]!.charCodeAt(0) + 1)}`;
  const m = /^([A-Za-z])(\d+)$/.exec(t);
  if (m) {
    const letter = m[1]!;
    if (section) {
      if (/[Zz]/.test(letter)) return null;
      return `${String.fromCharCode(letter.charCodeAt(0) + 1)}1`;
    }
    return `${letter}${Number(m[2]) + 1}`;
  }
  if (!section && /^\d+$/.test(t)) return padPart(String(Number(t) + 1));
  return null;
}

/** A housing's next number: 1 → 2, 09 → 10, 1A → 1B (Robert's MCC-1A, MCC-1B). Null when there is none. */
export function nextNumber(num: string): string | null {
  const t = cleanPart(num);
  if (/^\d+$/.test(t)) return String(Number(t) + 1);
  const m = /^(\d+)([A-Ya-y])$/.exec(t);
  if (m) return `${m[1]}${String.fromCharCode(m[2]!.charCodeAt(0) + 1)}`;
  return null;
}

/** The form after a save: every answer kept, the position (or the housing number) stepped up. */
export function stepForm(f: IdForm, how: "next" | "section"): IdForm | null {
  if (cleanPart(f.pos)) {
    const p = nextPosition(f.pos, how === "section");
    return p ? { ...f, pos: p } : null;
  }
  if (how === "section") return null;
  const n = nextNumber(f.num);
  return n ? { ...f, num: n } : null;
}

export const RANGE_MAX = 60;

// ---- Rev 5: Copy Down and the field answers (ASSET_ID_SHEET_REV5_WORDS.md, 2026-10-22) ----

/** The four answers judged at each asset. Copy Down never carries them. */
export const ANSWER_FIELDS = ["passive", "active", "installed", "usedPer"] as const;

/** The cell Copy Down steps: the END Location when the row has one, else the Equipment Number. */
export function steppedField(f: Pick<IdForm, "pos" | "loc">): "pos" | "num" {
  return cleanPart(f.pos) && !isOutside(f) ? "pos" : "num";
}

/** Copy Down: the row stepped once, the four field answers blank. Null when nothing can step. */
export function copyDownForm(f: IdForm): IdForm | null {
  const next = stepForm(f, "next");
  if (!next) return null;
  // Each piece of gear has its own door name, so the tag never copies down.
  return { ...next, tag: "", passive: "", active: "", installed: "", usedPer: "" };
}

/**
 * Copy Down To: the row stepped again and again until the stepped cell reads `to` (D6, or 22).
 * Up to RANGE_MAX rows. Empty when `to` is never reached.
 */
export function copyDownSeries(f: IdForm, to: string): IdForm[] {
  const field = steppedField(f);
  const target = padPart(to).toUpperCase();
  if (!target) return [];
  const out: IdForm[] = [];
  let cur: IdForm = f;
  for (let i = 0; i < RANGE_MAX; i++) {
    const next = copyDownForm(cur);
    if (!next) return [];
    out.push(next);
    if (padPart(next[field]).toUpperCase() === target) return out;
    cur = next;
  }
  return [];
}

/** A new type or room on the new row starts at the next free Equipment Number there: PNL-03 when 01 and 02 exist. */
export function nextFreeNumber(site: string, f: IdForm, classes: readonly ClassOption[], taken: ReadonlySet<string>): string {
  for (let n = 1; n < 100; n++) {
    const id = assetIdOf(site, { ...f, num: String(n), pos: "" }, classes);
    if (!id || !taken.has(id)) return String(n);
  }
  return "1";
}

// ---- Rev 6: double-tap an empty cell to fill it from above (ASSET_ID_SHEET_REV6_WORDS.md, 2026-10-22) ----

/** The cells a double-tap fills. The four field answers are never filled: each is judged at the asset. */
export const FILL_FIELDS = ["loc", "locNum", "type", "num", "pos", "volt", "cond", "oos"] as const;
export type FillField = (typeof FILL_FIELDS)[number];

/**
 * Double-tap an empty cell on the new row: what goes in it, from the row right above.
 * Location, Loc #, Type, Voltage, Conductor, Out of Service copy. END steps (C6 to C7, A2a to A2b).
 * Equip # copies inside a housing (the row above has an END Location), else steps (PNL-01 to PNL-02);
 * a different type or room on this row starts at the next free number there.
 * `stepped` says the value counted up: that cell gets the red outline. Null value: nothing to put in.
 */
export function fillFromAbove(
  cell: FillField,
  row: IdForm,
  above: IdForm,
  nextFree: () => string,
): { value: string | null; stepped: boolean; why?: string } {
  if (cell === "num") {
    const otherPlace = Boolean(row.type) && (row.type !== above.type || row.loc !== above.loc || cleanPart(row.locNum) !== cleanPart(above.locNum));
    if (otherPlace) return { value: nextFree(), stepped: true };
    if (cleanPart(above.pos)) return above.num ? { value: above.num, stepped: false } : { value: null, stepped: false, why: "The cell above is empty." };
    const n = nextNumber(above.num);
    return n ? { value: n, stepped: true } : { value: null, stepped: false, why: "The Equipment Number above can't count up." };
  }
  if (cell === "pos") {
    const p = cleanPart(above.pos) ? nextPosition(above.pos, false) : null;
    return p ? { value: p, stepped: true } : { value: null, stepped: false, why: "The row above has no END Location to step. One tap to type one." };
  }
  const v = above[cell];
  return v ? { value: v, stepped: false } : { value: null, stepped: false, why: "The cell above is empty." };
}

/**
 * Field definitions (approved by Robert 2026-10-23, 6:25 AM). Tap a column header to see one.
 * Exposure is a ladder (Robert, 2026-10-23, 6:37 AM): Active YES means Passive YES. Active NO leaves Passive a question.
 */
export const FIELD_DEFINITIONS: Record<"volt" | "cond" | "passive" | "active" | "installed" | "usedPer" | "oos", { title: string; text: string }> = {
  volt: { title: "Nominal System Voltage", text: "Nameplate or schedule voltage. Phase to phase at 208 V and up. Under 50 V only if nothing inside is 50 V or more." },
  cond: { title: "Conductor", text: "Fixed: wired in place. Moveable: cord, plug or flexible lead." },
  passive: { title: "Passive Exposure", text: "Conceivable contact. YES: an exposed conductor is present but not within 12 in, such as behind a barrier, deep in the enclosure, or seen through a gap. NO: doors closed, covers on. Active YES sets this to YES." },
  active: { title: "Active Exposure", text: "Credible contact. YES: door open or cover missing, and an exposed conductor is within 12 in. At 120 V: Avoid Contact, so YES if it's within reach. NO: doors closed, covers on." },
  installed: { title: "Properly Installed", text: "YES: mounted, covers and dead fronts on, knockouts filled, enclosure intact, right for the location. NO: any of those missing." },
  usedPer: { title: "Used Per Instructions", text: "YES: used as listed and labeled, within rating. NO: over rating, interlock bypassed, used for storage or as a junction box." },
  oos: { title: "Out of Service", text: "YES: disconnected and taken out of use on purpose. NO: in service, even if idle." },
};
export type DefinedField = keyof typeof FIELD_DEFINITIONS;

/**
 * Answering Active Exposure. Credible contact is always conceivable, so Active YES sets Passive YES.
 * Active NO leaves Passive as it is: still a question (exposed but out of reach, or closed and covered).
 */
export function withExposure<T extends Pick<IdForm, "passive" | "active">>(f: T, active: string): T {
  return active === "YES" ? { ...f, active, passive: "YES" } : { ...f, active };
}

/** The cells that make the Asset ID. */
export const ID_FIELDS = ["loc", "locNum", "type", "num", "pos"] as const;

/**
 * A saved asset's ID cells can be retyped while nothing carries the ID yet: made on the iPad, label not
 * printed, not on the map, no study (ASSET_ID_SHEET_REV5_WORDS.md section 5). Assets pushed from the
 * workbook had their labels printed by the label script, so they stay locked.
 */
export function canRetypeId(d: Record<string, unknown>, mapped: boolean): boolean {
  return d.source === "Asset ID page" && !d.qrLabelPrintedAt && d.isRemoved !== true && !mapped && !d.afStudyId && typeof d.incidentEnergyCal !== "number";
}

/** The Remove confirm: what removing this asset touches, in plain words. Nothing blocks it. */
export function removeWarning(info: { mapped: boolean; studied: boolean; printed: boolean }): string {
  const parts: string[] = [];
  if (info.mapped) parts.push("It is on the map. Removing takes it off the map and out of Collect.");
  if (info.studied) parts.push("It has arc flash study values. They stay in the record with the removed asset.");
  if (info.printed) parts.push("Its QR label is printed: pull the label from the gear.");
  parts.push("It stays in the record as removed. Show Removed brings it back.");
  return parts.join(" ");
}

/** Add A Range: B1 to B9 → B1, B2 … B9. Same letter (or plain numbers), counting up. Empty when it cannot. */
export function expandRange(from: string, to: string): string[] {
  const a = cleanPart(from).toUpperCase();
  const b = cleanPart(to).toUpperCase();
  const ma = /^([A-Z]?)(\d+)$/.exec(a);
  const mb = /^([A-Z]?)(\d+)$/.exec(b);
  if (!ma || !mb || ma[1] !== mb[1]) return [];
  const lo = Number(ma[2]);
  const hi = Number(mb[2]);
  if (hi < lo || hi - lo + 1 > RANGE_MAX) return [];
  const out: string[] = [];
  for (let i = lo; i <= hi; i++) out.push(ma[1] ? `${ma[1]}${i}` : padPart(String(i)));
  return out;
}

// ---- The list (section 4) ----

export interface ListAsset {
  assetId: string;
  /** Sites Equipment Tag, when the asset has one. Find matches it too. */
  siteEquipmentTag?: string;
  location: string;
  equipmentType: string;
  isRemoved: boolean;
}

export interface ListHousing {
  id: string;
  /** The housing's own record, or null when only its positions are made. */
  self: ListAsset | null;
  kids: ListAsset[];
}

export interface ListRoom {
  room: string;
  housings: ListHousing[];
  count: number;
}

/** The room heading for an asset: ER1, EA2, Outside. From the location, else the ID. */
export function listRoomOf(a: Pick<ListAsset, "assetId" | "location">): string {
  const m = /:\s*(E[RAL]\d+[a-z]?)\s*$/i.exec(a.location);
  if (m) return m[1]!;
  const p = a.assetId.split("-")[1] ?? "";
  return /^E[RAL]\d/i.test(p) ? p : "Outside";
}

/** The housing an ID sits in, by the naming rule: an ID with a position is SS-EEL-CODE-NN-POS. */
export function housingIdOf(assetId: string): string {
  const p = assetId.split("-");
  return /^E[RAL]\d/i.test(p[1] ?? "") && p.length > 4 ? p.slice(0, 4).join("-") : assetId;
}

/** The site's assets grouped by room, each housing with its positions. A search narrows by ID. */
/** Letters and numbers only, upper case: "LP-1A" and "lp 1a" both read LP1A, for Find. */
export function squash(s: string): string {
  return String(s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

export function groupAssets(list: readonly ListAsset[], query: string): ListRoom[] {
  const q = query.trim().toUpperCase();
  const qTag = squash(query);
  const rooms = new Map<string, Map<string, ListHousing>>();
  const sorted = [...list].sort((x, y) => x.assetId.localeCompare(y.assetId, undefined, { numeric: true }));
  for (const a of sorted) {
    if (q && !a.assetId.toUpperCase().includes(q) && !(qTag && squash(a.siteEquipmentTag ?? "").includes(qTag))) continue;
    const room = listRoomOf(a);
    if (!rooms.has(room)) rooms.set(room, new Map());
    const hid = housingIdOf(a.assetId);
    const m = rooms.get(room)!;
    if (!m.has(hid)) m.set(hid, { id: hid, self: null, kids: [] });
    if (hid === a.assetId) m.get(hid)!.self = a;
    else m.get(hid)!.kids.push(a);
  }
  const order = (r: string) => (r === "Outside" ? "0" : `1${r}`);
  return [...rooms.entries()]
    .sort((x, y) => order(x[0]).localeCompare(order(y[0]), undefined, { numeric: true }))
    .map(([room, m]) => ({ room, housings: [...m.values()], count: [...m.values()].reduce((n, h) => n + (h.self ? 1 : 0) + h.kids.length, 0) }));
}

/** The form a room on the Map starts from: Add Asset with the room filled in. */
export function formForRoom(room: string, base: IdForm = BLANK_FORM): IdForm {
  const m = /^(ER|EA)(\d+[a-z]?)$/i.exec(room.trim());
  if (!m) return { ...base };
  return { ...base, loc: m[1]!.toUpperCase() === "EA" ? "Electrical Area" : "Electrical Room", locNum: m[2]!, type: "", num: "", pos: "" };
}

/**
 * Why Remove is off, in words, or null when it is on (section 8). An asset on the map, in Collect,
 * holding a Model Tag, or with a study published stays: mark it Out of Service instead.
 */
export function removeBlockedWhy(info: { mapped: boolean; studied: boolean }): string | null {
  if (info.studied && info.mapped) return "It is on the map and in the study, so Remove is off. Mark it Out of Service instead.";
  if (info.studied) return "It has a study, so Remove is off. Mark it Out of Service instead.";
  if (info.mapped) return "It is on the map, so Remove is off. Mark it Out of Service instead.";
  return null;
}

// ---- Change history (ENGINEERING_REVIEW_I11.md Tier 1, approved 2026-10-21) ----

export type ChangeAction = "Created" | "Edited" | "Removed" | "Restored" | "Label Printed" | "Dropped";

/** Who changed it, from which device, and when. */
export interface ChangeStamp extends Stamp {
  deviceName: string;
}

/** Fields that say who and when. They are the history record's own columns, not changes. */
const STAMP_FIELDS = new Set(["updatedAt", "updatedBy", "updatedByName", "createdAt", "createdBy", "createdByName"]);

/**
 * The record ID: the Asset ID, two underscores, the time as 17 digits and 4 random characters.
 * Fixed when the change is saved, so a resend from the Outbox lands on the same record.
 */
export function changeIdOf(assetId: string, at: string, rand: string = Math.random().toString(36).slice(2, 6).padEnd(4, "0")): string {
  const digits = at.replace(/\D/g, "").padEnd(17, "0").slice(0, 17);
  return `${assetId}__${digits}-${rand.toLowerCase().replace(/[^0-9a-z]/g, "0").padEnd(4, "0").slice(0, 4)}`;
}

/** What changed, field by field, before and after. A field with no before reads null. */
export function changesOf(before: Record<string, unknown> | null, data: Record<string, unknown>): Record<string, { before: unknown; after: unknown }> {
  const out: Record<string, { before: unknown; after: unknown }> = {};
  for (const [k, v] of Object.entries(data)) {
    if (STAMP_FIELDS.has(k)) continue;
    const was = before?.[k] ?? null;
    if (before && was === v) continue;
    out[k] = { before: was, after: v ?? null };
  }
  return out;
}

/** The action a save is, read from what it writes. */
export function actionOf(before: Record<string, unknown> | null, data: Record<string, unknown>): ChangeAction {
  if (!before || (typeof data.createdAt === "string" && !("updatedAt" in data))) return "Created";
  if (data.isRemoved === true && before.isRemoved !== true) return "Removed";
  if (data.isRemoved === false && before.isRemoved === true) return "Restored";
  const keys = Object.keys(data).filter((k) => !STAMP_FIELDS.has(k));
  if (keys.length === 1 && keys[0] === "qrLabelPrintedAt") return "Label Printed";
  return "Edited";
}

/** One history record, named like the survey's records. Never edited once saved. */
export function changeRecordOf(assetId: string, action: ChangeAction, changes: Record<string, { before: unknown; after: unknown }>, s: ChangeStamp, note = ""): Record<string, unknown> {
  return {
    "Asset ID": assetId,
    "Site Number": s.siteNumber,
    Action: action,
    Changes: changes,
    Note: note,
    "Badge ID": s.badgeId,
    "Holder Name": s.holderName,
    "Device Name": s.deviceName,
    "Changed At": s.at,
  };
}

// ---- The Creator grid (ASSET_IDS_SECTION_WORDS.md rev 3.2, approved 2026-10-21) ----

/** The workbook's site list (AI:AJ): Site Identifier as the workbook writes it, by site number. */
const SITE_PLACES: Record<string, string> = {
  "00": "Nicholasville, KY", "01": "Springfield, KY", "02": "Flemingsburg, KY", "03": "Mankato, MN", "04": "Shipshewana, IN",
  "05": "Billings West Feeds", "06": "Buffalo, TX", "07": "Iowa City, IA", "08": "Mendota, IL", "09": "Alexandria, MN",
  "10": "Baconton, GA", "11": "Eden, NC", "12": "Appleton, WI", "13": "Beloit, KS", "14": "Bismarck, ND",
  "15": "Botkins, OH", "16": "Columbus, NE", "17": "Dyersville, IA", "18": "Fort Collins, CO", "19": "Huron, SD",
  "20": "Rapid City, SD", "21": "Sioux City, IA", "22": "Worthington Feed", "23": "Fort Worth, TX", "24": "Montgomery, AL",
  "25": "Pittsburgh, KS", "26": "Versailles, KY", "27": "Whitewood, SD", "28": "Worthington Block", "29": "Lancaster, PA",
};

/** "10 (Baconton, GA)". A site not in the workbook's list reads with its name from the Sites page. */
export function siteIdentifier(siteNumber: string, siteName: string): string {
  return `${siteNumber} (${SITE_PLACES[siteNumber] ?? siteName})`;
}

/** EEL ID, column F: the location code and number (ER1). Blank outside or until filled in. */
export function eelIdOf(f: Pick<IdForm, "loc" | "locNum">): string {
  return isOutside(f) ? "" : roomOf(f);
}

/** EET ID, column I: the class code and padded number (MCC-01). */
export function eetIdOf(f: Pick<IdForm, "type" | "num">, classes: readonly ClassOption[]): string {
  const code = classCode(f.type, classes);
  const num = padPart(f.num);
  return code && num ? `${code}-${num}` : "";
}

/** The workbook's RPN colors (conditional format on U and Y): 1 to 3 LOW, 4 to 7 MEDIUM, 8 to 12 HIGH. */
export function rpnBand(rpn: number | null): "low" | "med" | "high" | "" {
  if (rpn === null) return "";
  return rpn <= 3 ? "low" : rpn <= 7 ? "med" : "high";
}

/**
 * Over 50 V and under 208 V takes a shock label in place of the arc flash work, and no QR label
 * (Robert, 2026-10-28 10:46 AM). Under 50 V and 208 V and up are left as they are.
 */
export function shockLabelOnly(d: { nominalVoltage?: unknown }): boolean {
  const v = voltsOf(String(d.nominalVoltage ?? ""));
  return v !== null && v > 50 && v < 208;
}
